import { AUTHENTICATED } from '@mkcore/platform/schema';
import { CsvEnum, HistoryEntry, Id, IsoDate, IsoDateTime, ListQuery, Paginated, QueryBool, QueryVersion, RecordLink, RecordLinks, defineAction, defineEvent, updateFields, z } from '@mkcore/shared';
import { projectPermissions as P } from './permissions';

// Namespace project (docs/01-architectuur/05-api-en-mcp.md): projects under an organisation and engagement, with
// subprojects (CO-0189, CO-1268), work types (CO-0190), colours (CO-0195), status (CO-0193) and favourites (CO-0191).

export const PROJECT_STATUSES = ['active', 'on_hold', 'completed'] as const;
/** The health of a project (CO-0197): set by the latest weekly status, or by a person. */
export const PROJECT_HEALTHS = ['on_track', 'at_risk', 'behind'] as const;
export const ProjectHealth = z.enum(PROJECT_HEALTHS);
export const ProjectStatus = z.enum(PROJECT_STATUSES);
/** The colours of the MKC palette a project can take (docs/01-architectuur/11-frontend.md); stored as the hex value. */
export const PROJECT_COLOURS = ['#B08D57', '#1D76DB', '#0E8A16', '#D73A4A', '#7057FF', '#E4572E', '#0075CA', '#5C6B73', '#C2185B', '#00897B'] as const;
const Colour = z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'A hex colour such as #B08D57');
const ExpectedVersion = z.number().int().positive().optional().describe('Version you read; the update fails if it changed');
const Minutes = z.number().int().min(0).max(100_000_000);
const Cents = z.number().int().min(0).max(1_000_000_000);

export const Project = z
  .object({
    id: Id,
    parentId: Id.nullable().describe('Set on a subproject (addition, extra work, partial assignment)'),
    parentName: z.string().nullable().describe('Name of the main project of a subproject'),
    organisationId: Id.nullable().describe('null for an internal project'),
    organisationName: z.string().nullable(),
    engagementId: Id.nullable(),
    engagementName: z.string().nullable(),
    name: z.string(),
    code: z.string().nullable().describe('Short code, for example ACME-PLT'),
    colour: z.string(),
    status: ProjectStatus.describe('Time can only be booked on an active project (CO-0193)'),
    billableDefault: z.boolean().nullable().describe('null inherits from the parent, the engagement or true'),
    effectiveBillableDefault: z.boolean(),
    startsOn: IsoDate.nullable(),
    endsOn: IsoDate.nullable(),
    budgetMinutes: z.number().int().nullable(),
    budgetCents: z.number().int().nullable(),
    notes: z.string(),
    health: ProjectHealth.nullable().describe('From the latest weekly status, or set by a person (CO-0197)'),
    healthNote: z.string().describe('One line on the health'),
    healthAt: IsoDateTime.nullable(),
    isTemplate: z.boolean().describe('A template to copy new projects from (CO-0198): no time booked on it, left out of lists'),
    links: z.array(RecordLink).describe('Links to repositories, SharePoint, memory and issue views (CO-0487)'),
    favourite: z.boolean().describe('Marked as favourite by the caller'),
    subprojectCount: z.number().int(),
    archivedAt: IsoDateTime.nullable(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
    version: z.number().int(),
    url: z.string(),
  })
  .openapi('Project');

const projectFields = {
  name: z.string().trim().min(1).max(200),
  code: z.string().trim().max(20).nullable().optional(),
  colour: Colour.optional(),
  status: ProjectStatus.default('active'),
  billableDefault: z.boolean().nullable().optional(),
  startsOn: IsoDate.nullable().optional(),
  endsOn: IsoDate.nullable().optional(),
  budgetMinutes: Minutes.nullable().optional(),
  budgetCents: Cents.nullable().optional(),
  notes: z.string().max(20_000).optional(),
  isTemplate: z.boolean().optional().describe('A template for new projects (CO-0198)'),
  links: RecordLinks.optional().describe('The whole list: repo, sharepoint, memory, issues or other, with label and url (CO-0487)'),
};

export const listProjects = defineAction({
  name: 'project.project.list',
  method: 'get',
  path: '/project/projects',
  permission: P.keys.read,
  input: ListQuery.extend({
    q: z.string().max(200).optional().describe('Search in name and code'),
    organisationId: Id.optional(),
    engagementId: Id.optional(),
    parentId: Id.optional().describe('Subprojects of this project'),
    topLevel: QueryBool.optional().describe('Only projects without a parent'),
    status: CsvEnum(PROJECT_STATUSES).optional(),
    bookable: QueryBool.optional().describe('Only active, not archived projects: the choice list for time entries'),
    favourite: QueryBool.optional(),
    health: CsvEnum(PROJECT_HEALTHS).optional().describe('For example at_risk,behind'),
    template: QueryBool.optional().describe('true: only templates. Default: templates are left out, except as subprojects of a parentId'),
    includeArchived: QueryBool.optional(),
  }),
  output: Paginated(Project),
  summary: 'List projects; bookable=true gives the choice list for booking time',
  description:
    'Ordered by main project: each main project is followed by its subprojects, and a subproject that matches without its main project stands at the place of its main project. A page never splits a main project from its subprojects, so limit counts main projects.',
});

export const getProject = defineAction({
  name: 'project.project.get',
  method: 'get',
  path: '/project/projects/{id}',
  permission: P.keys.read,
  input: z.object({ id: Id }),
  output: Project,
  etag: true,
});

export const createProject = defineAction({
  name: 'project.project.create',
  method: 'post',
  path: '/project/projects',
  permission: P.keys.create,
  input: z
    .object({
      organisationId: Id.nullable().optional().describe('Omit for an internal project, such as acquisition'),
      engagementId: Id.nullable().optional().describe('The engagement the project belongs to; its organisation must match'),
      parentId: Id.nullable().optional().describe('Make a subproject under this project; it inherits organisation and engagement'),
      ...projectFields,
    })
    .strict(),
  output: Project,
  summary: 'Add a project, an internal project or a subproject',
  description: 'A subproject takes the organisation and engagement of its parent; only one level of subprojects. Fails with organisation-archived or engagement-archived.',
});

export const updateProject = defineAction({
  name: 'project.project.update',
  method: 'patch',
  path: '/project/projects/{id}',
  permission: P.keys.update,
  input: z
    .object({
      id: Id,
      organisationId: Id.nullable()
        .optional()
        .describe('Move the project and its subprojects to this organisation, or null for internal; without engagementId it leaves its engagement. Its unbilled hours move along'),
      engagementId: Id.nullable().optional(),
      parentId: Id.nullable()
        .optional()
        .describe('Link the project under this main project of the same organisation as a subproject, or null to make a subproject a project of its own'),
      ...(updateFields(projectFields) as {
        [K in keyof typeof projectFields]: z.ZodOptional<(typeof projectFields)[K]>;
      }),
      health: ProjectHealth.nullable().optional().describe('Set the health by hand; the next weekly status sets it again'),
      healthNote: z.string().max(500).optional(),
      expectedVersion: ExpectedVersion,
    })
    .strict(),
  output: Project,
  etag: true,
  description:
    'A linked subproject takes the engagement of its main project and keeps its hours; only one level of subprojects. A project of its own can move to another organisation (#181); its subprojects and unbilled hours follow, invoiced hours stay. Fails with parent-organisation, parent-template, subproject-depth, subproject-self, subproject-inherits, engagement-organisation, organisation-archived or project-archived.',
});

export const archiveProject = defineAction({
  name: 'project.project.archive',
  method: 'post',
  path: '/project/projects/{id}/archive',
  permission: P.keys.update,
  input: z.object({ id: Id }),
  output: Project,
  successStatus: 200,
  summary: 'Archive a project and its subprojects: out of the choice lists, still in reports (CO-0191)',
});

export const restoreProject = defineAction({
  name: 'project.project.restore',
  method: 'post',
  path: '/project/projects/{id}/restore',
  permission: P.keys.update,
  input: z.object({ id: Id }),
  output: Project,
  successStatus: 200,
});

export const setProjectFavourite = defineAction({
  name: 'project.project.favourite',
  method: 'post',
  path: '/project/projects/{id}/favourite',
  permission: AUTHENTICATED,
  input: z.object({ id: Id, favourite: z.boolean() }),
  output: Project,
  successStatus: 200,
  summary: 'Mark a project as a favourite of the caller, or unmark it',
});

// ---------- work types (CO-0190) ----------

export const WorkType = z
  .object({
    id: Id,
    name: z.string(),
    billableDefault: z.boolean(),
    position: z.number().int(),
    archivedAt: IsoDateTime.nullable(),
    createdAt: IsoDateTime,
    version: z.number().int(),
  })
  .openapi('WorkType');

export const listWorkTypes = defineAction({
  name: 'project.workType.list',
  method: 'get',
  path: '/project/work-types',
  permission: AUTHENTICATED,
  input: z.object({ includeArchived: QueryBool.optional() }),
  output: z.object({ items: z.array(WorkType) }),
  summary: 'The company-wide work types (development, advice, support, travel), in order',
});

export const createWorkType = defineAction({
  name: 'project.workType.create',
  method: 'post',
  path: '/project/work-types',
  permission: P.keys.create,
  input: z.object({ name: z.string().trim().min(1).max(100), billableDefault: z.boolean().default(true) }).strict(),
  output: WorkType,
});

export const updateWorkType = defineAction({
  name: 'project.workType.update',
  method: 'patch',
  path: '/project/work-types/{id}',
  permission: P.keys.update,
  input: z
    .object({
      id: Id,
      name: z.string().trim().min(1).max(100).optional(),
      billableDefault: z.boolean().optional(),
      position: z.number().int().min(0).optional(),
      expectedVersion: ExpectedVersion,
    })
    .strict(),
  output: WorkType,
  etag: true,
});

export const archiveWorkType = defineAction({
  name: 'project.workType.archive',
  method: 'post',
  path: '/project/work-types/{id}/archive',
  permission: P.keys.update,
  input: z.object({ id: Id }),
  output: WorkType,
  successStatus: 200,
});

export const restoreWorkType = defineAction({
  name: 'project.workType.restore',
  method: 'post',
  path: '/project/work-types/{id}/restore',
  permission: P.keys.update,
  input: z.object({ id: Id }),
  output: WorkType,
  successStatus: 200,
});

// ---------- events ----------

export const projectChanged = defineEvent({
  type: 'project.project.changed',
  version: 1,
  payload: z.object({ projectId: Id, organisationId: Id.nullable(), change: z.enum(['created', 'updated', 'archived', 'restored']) }),
});

/**
 * A project moved to another organisation or engagement (#181), also a subproject that followed its parent. The time
 * domain moves the unbilled hours of the project along; the payload says where the project is now.
 */
export const projectMoved = defineEvent({
  type: 'project.project.moved',
  version: 1,
  payload: z.object({ projectId: Id, organisationId: Id.nullable(), engagementId: Id.nullable() }),
});

export const workTypeChanged = defineEvent({
  type: 'project.workType.changed',
  version: 1,
  payload: z.object({ workTypeId: Id, change: z.enum(['created', 'updated', 'archived', 'restored']) }),
});

export const projectActions = [
  listProjects,
  getProject,
  createProject,
  updateProject,
  archiveProject,
  restoreProject,
  setProjectFavourite,
  listWorkTypes,
  createWorkType,
  updateWorkType,
  archiveWorkType,
  restoreWorkType,
];

// ---------- tasks (U5, CO-0435, CO-0434, CO-0438, CO-0445) ----------

export const TASK_STATUSES = ['todo', 'in_progress', 'done', 'cancelled'] as const;
export const TaskStatus = z.enum(TASK_STATUSES);
export const TASK_PRIORITIES = ['urgent', 'high', 'normal', 'low'] as const;
export const TaskPriority = z.enum(TASK_PRIORITIES);
/** The records a task can point at; each is resolved through its own domain (configureTaskLinks). */
export const TASK_LINKS = ['lead', 'proposal', 'agreement', 'invoice'] as const;
export const MAX_CHECKLIST = 100;
export const BLOCKER_TYPES = ['task', 'issue', 'milestone'] as const;

const ChecklistItem = z.object({ id: z.string(), text: z.string(), done: z.boolean() }).openapi('TaskChecklistItem');
const LinkRef = z.object({ id: Id, label: z.string().nullable().describe('null when you may not read it') }).nullable();
const BlockerRef = z
  .object({
    id: Id.describe('The dependency, for project.dependency.delete'),
    type: z.enum(BLOCKER_TYPES),
    blockerId: Id,
    label: z.string().nullable().describe('null when you may not read it'),
    open: z.boolean().describe('Still open: a task not done or cancelled, a milestone not reached, an open issue'),
    dueOn: IsoDate.nullable(),
  })
  .openapi('TaskBlocker');

/** What a task can be on (#301): a project, or without one an engagement, organisation or lead. */
export const TASK_PARENTS = ['project', 'engagement', 'organisation', 'lead'] as const;
const TaskParent = z
  .object({ type: z.enum(TASK_PARENTS), id: Id, label: z.string().nullable().describe('null when you may not read it') })
  .openapi('TaskParent');

export const Task = z
  .object({
    id: Id,
    number: z.number().int(),
    key: z.string().describe('T-12: the number to refer to in chat and mail'),
    projectId: Id.nullable().describe('null for a task on an engagement, organisation or lead without a project (#301)'),
    projectName: z.string().nullable(),
    organisationId: Id.nullable().describe('The organisation of the project, engagement or lead, or the one the task is on'),
    organisationName: z.string().nullable().describe('null when you may not read it'),
    engagementId: Id.nullable(),
    engagementName: z.string().nullable().describe('null when you may not read it'),
    parent: TaskParent.describe('What the task is on: its project, else its engagement, organisation or lead'),
    milestoneId: Id.nullable(),
    milestoneTitle: z.string().nullable(),
    title: z.string(),
    description: z.string().describe('Markdown'),
    ownerId: Id.nullable().describe('A person or an agent'),
    ownerName: z.string().nullable(),
    ownerKind: z.enum(['human', 'agent']).nullable(),
    status: TaskStatus,
    priority: TaskPriority,
    dueOn: IsoDate.nullable(),
    overdue: z.boolean().describe('Past its due date and still open'),
    estimateMinutes: z.number().int().nullable(),
    spentMinutes: z.number().int().describe('Booked time on this task (time entries): everyone\'s, or only your own when spentEveryone is false'),
    spentEveryone: z.boolean().describe('false: spentMinutes is only your own time, you lack time:readAll (CO-0275)'),
    overEstimate: z.boolean().describe('More time booked than estimated, the time on its subtasks included (CO-0275, CO-0443)'),
    checklist: z.array(ChecklistItem),
    progress: z.number().int().nullable().describe('Percent done of the checklist lines and the subtasks together (cancelled subtasks do not count); null without either'),
    lead: LinkRef,
    proposal: LinkRef,
    agreement: LinkRef,
    invoice: LinkRef,
    blockedBy: z.array(BlockerRef).describe('What this task waits for (CO-0437)'),
    blocking: z.array(z.object({ id: Id, key: z.string(), title: z.string() })).describe('The tasks that wait for this one; filled by project.task.get'),
    warnings: z.array(z.object({ code: z.string(), message: z.string() })).describe('Such as a due date before that of an open blocker'),
    doneAt: IsoDateTime.nullable(),
    clientTask: z.boolean().describe('A task for the client, shown in the portal (CO-0666)'),
    clientNote: z.string().describe('What the client said when it finished the task in the portal'),
    clientDoneByName: z.string().nullable(),
    nextAction: z.string().describe('Markdown: what the owner, a person or an agent, does next (CO-1377)'),
    requestedById: Id.nullable().describe('The person who gave the task to an agent; null when a person owns it'),
    requestedByName: z.string().nullable(),
    parentTaskId: Id.nullable().describe('The task this one is a subtask of (CO-0443); null for a task on its own or one with subtasks'),
    parentKey: z.string().nullable().describe('T-12 of the parent task'),
    parentTitle: z.string().nullable(),
    subtasks: z
      .object({ total: z.number().int().describe('Subtasks that are not cancelled'), done: z.number().int().describe('Of those, the ones that are done') })
      .describe('The subtasks of this task, listed by project.task.list with parentTaskId; both 0 without subtasks'),
    subtaskSpentMinutes: z.number().int().describe('Booked time on its subtasks, counted like spentMinutes; the time on the whole task is spentMinutes plus this'),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
    version: z.number().int(),
    url: z.string(),
  })
  .openapi('Task');

const ChecklistInput = z
  .array(z.object({ id: z.string().max(40).optional().describe('Keep the id of an existing line'), text: z.string().trim().min(1).max(500), done: z.boolean().default(false) }))
  .max(MAX_CHECKLIST)
  .describe('Replaces the whole list; lines without an id are new');

const taskFields = {
  engagementId: Id.nullable().optional().describe('The task is on this engagement; with a project, that of the project'),
  organisationId: Id.nullable().optional().describe('The task is on this organisation; with a project or engagement, theirs'),
  title: z.string().trim().min(1).max(300),
  description: z.string().max(50_000).optional(),
  milestoneId: Id.nullable().optional().describe('A milestone of the same project (project.milestone.list)'),
  ownerId: Id.nullable().optional().describe('A person or agent (admin.user.list); null for nobody'),
  status: TaskStatus.optional(),
  priority: TaskPriority.optional(),
  dueOn: IsoDate.nullable().optional(),
  estimateMinutes: Minutes.nullable().optional(),
  checklist: ChecklistInput.optional(),
  leadId: Id.nullable().optional().describe('Without project, engagement or organisation the task is on this lead'),
  proposalId: Id.nullable().optional(),
  agreementId: Id.nullable().optional(),
  invoiceId: Id.nullable().optional(),
  clientTask: z.boolean().optional().describe('A task for the client, such as an acceptance test; the client finishes it in the portal (CO-0666)'),
  nextAction: z.string().max(2000).optional().describe('Markdown: what the owner, a person or an agent, does next. For an agent this is the start of its work: its claim takes it, with the description, as acceptance criteria (CO-1377)'),
  parentTaskId: Id.nullable().optional().describe('Make it a subtask of this task, on the same project, engagement, organisation or lead; null makes it a task on its own. One level: the parent is no subtask, and a task with subtasks does not become one. An agent adds subtasks only under a task it owns, owned by itself or the requester of the parent (CO-0443)'),
};

export const listTasks = defineAction({
  name: 'project.task.list',
  method: 'get',
  path: '/project/tasks',
  permission: P.keys.read,
  input: ListQuery.extend({
    projectId: Id.optional().describe('Tasks of this project and its subprojects'),
    engagementId: Id.optional().describe('Tasks on this engagement or on its projects'),
    organisationId: Id.optional().describe('Tasks on this organisation, or on its engagements, projects and leads'),
    leadId: Id.optional().describe('Tasks on or linked to this lead'),
    withoutProject: QueryBool.optional().describe('Only tasks on an engagement, organisation or lead without a project'),
    milestoneId: Id.optional(),
    parentTaskId: Id.optional().describe('Only the subtasks of this task (CO-0443); add status for the closed ones too'),
    ownerId: Id.optional(),
    mine: QueryBool.optional().describe('Only your own tasks'),
    unassigned: QueryBool.optional(),
    status: CsvEnum(TASK_STATUSES).optional().describe('Default: todo and in_progress'),
    priority: CsvEnum(TASK_PRIORITIES).optional(),
    dueFrom: IsoDate.optional(),
    dueTo: IsoDate.optional(),
    overdue: QueryBool.optional(),
    clientTask: QueryBool.optional().describe('true: only tasks that lie with the client (client tasks); false: only internal tasks'),
    q: z.string().max(200).optional().describe('Search in title and description, or a key such as T-12'),
    sort: z.enum(['due', 'priority', 'number']).default('due').describe('due: earliest first, without a date last'),
  }),
  output: Paginated(Task),
  summary: 'List tasks across projects, with filters (CO-0445)',
});

export const getTask = defineAction({
  name: 'project.task.get',
  method: 'get',
  path: '/project/tasks/{id}',
  permission: P.keys.read,
  input: z.object({ id: Id }),
  output: Task,
  etag: true,
});

export const taskHistory = defineAction({
  name: 'project.task.history',
  method: 'get',
  path: '/project/tasks/{id}/history',
  permission: P.keys.read,
  input: ListQuery.extend({ id: Id }),
  output: Paginated(HistoryEntry),
  summary: 'What changed on the task, when and by whom (a person or an agent), newest first: fields, status, comments, dependencies (CO-0551)',
});

export const createTask = defineAction({
  name: 'project.task.create',
  method: 'post',
  path: '/project/tasks',
  permission: P.keys.create,
  input: z.object({ projectId: Id.nullable().optional().describe('The project; leave out for a task on an engagement, organisation or lead'), ...taskFields }).strict(),
  output: Task,
  summary: 'Add a task: work that is not code, on a project, engagement, organisation or lead (CO-0435)',
  description:
    'Give at least one of projectId, engagementId, organisationId or leadId (task-needs-parent). Code work is an issue (dev.issue.create). Priority defaults to normal, status to todo. Time is booked only on a task with a project.',
});

export const updateTask = defineAction({
  name: 'project.task.update',
  method: 'patch',
  path: '/project/tasks/{id}',
  permission: P.keys.update,
  input: z
    .object({
      id: Id,
      projectId: Id.nullable().optional().describe('Move the task to another project, or null to take it off its project (it stays on the engagement or organisation); its milestone is cleared and its subtasks move along'),
      ...updateFields(taskFields),
      expectedVersion: ExpectedVersion,
    })
    .strict(),
  output: Task,
  etag: true,
});

export const deleteTask = defineAction({
  name: 'project.task.delete',
  method: 'delete',
  path: '/project/tasks/{id}',
  permission: P.keys.delete,
  input: z.object({ id: Id, expectedVersion: QueryVersion }),
  output: z.object({ ok: z.literal(true) }),
  etag: true,
  summary: 'Delete a task without booked time or subtasks; cancel it otherwise',
});

export const checkTaskItem = defineAction({
  name: 'project.task.check',
  method: 'post',
  path: '/project/tasks/{id}/checklist/{itemId}',
  permission: P.keys.update,
  input: z.object({ id: Id, itemId: z.string().max(40), done: z.boolean() }).strict(),
  output: Task,
  successStatus: 200,
  summary: 'Tick a line of the checklist of a task on or off (CO-0438)',
});

export const createDependency = defineAction({
  name: 'project.dependency.create',
  method: 'post',
  path: '/project/tasks/{id}/dependencies',
  permission: P.keys.update,
  input: z.object({ id: Id, blockerType: z.enum(BLOCKER_TYPES), blockerId: Id.describe('The task, issue (dev.issue.list) or milestone that blocks it') }).strict(),
  output: Task,
  summary: 'This task waits for a task, issue or milestone (CO-0437)',
  description: 'dependency-cycle when the task would then wait for itself; a due date before that of an open blocker gives a warning on the task.',
});

export const deleteDependency = defineAction({
  name: 'project.dependency.delete',
  method: 'delete',
  path: '/project/dependencies/{id}',
  permission: P.keys.update,
  input: z.object({ id: Id }),
  output: Task,
  summary: 'The task no longer waits for this blocker',
});

export const taskChanged = defineEvent({
  type: 'project.task.changed',
  version: 1,
  payload: z.object({ taskId: Id, projectId: Id.nullable(), change: z.enum(['created', 'updated', 'deleted']), status: TaskStatus.nullable() }),
});

export const taskAssigned = defineEvent({
  type: 'project.task.assigned',
  version: 1,
  payload: z.object({ taskId: Id, projectId: Id.nullable(), key: z.string(), title: z.string(), ownerId: Id, by: Id.nullable() }),
});

export const taskActions = [listTasks, getTask, createTask, updateTask, deleteTask, checkTaskItem, createDependency, deleteDependency];
export const taskEvents = [taskChanged, taskAssigned];

// ---------- milestones (U5, CO-0433) ----------

export const MILESTONE_STATUSES = ['planned', 'active', 'reached'] as const;
export const MilestoneStatus = z.enum(MILESTONE_STATUSES);

export const Milestone = z
  .object({
    id: Id,
    projectId: Id,
    projectName: z.string(),
    title: z.string(),
    description: z.string().describe('Markdown'),
    dueOn: IsoDate.nullable(),
    position: z.number().int(),
    status: MilestoneStatus.describe('reached: all its tasks are done or cancelled and its Forgejo milestones have no open issues'),
    reachedAt: IsoDateTime.nullable(),
    late: z.boolean().describe('Past its due date and not reached'),
    amountCents: z.number().int().nullable().describe('For a fixed price: the amount invoiced once accepted'),
    currency: z.string(),
    acceptedAt: IsoDateTime.nullable(),
    acceptedByName: z.string().nullable().describe('Who accepted it at the client'),
    acceptanceNote: z.string(),
    acceptedVia: z.enum(['person', 'portal']).nullable().describe('portal: the client accepted it itself (CO-0666)'),
    rejectedAt: IsoDateTime.nullable().describe('The client turned it down in the portal; a later acceptance clears it'),
    rejectedByName: z.string().nullable(),
    rejectionNote: z.string(),
    forgeMilestoneIds: z.array(Id).describe('Linked Forgejo milestones of the mirror (dev.milestone.list)'),
    taskCount: z.number().int(),
    openTaskCount: z.number().int(),
    openIssueCount: z.number().int().describe('Open issues in the linked Forgejo milestones you may read'),
    version: z.number().int(),
    url: z.string(),
  })
  .openapi('ProjectMilestone');

const milestoneFields = {
  title: z.string().trim().min(1).max(300),
  description: z.string().max(20_000).optional(),
  dueOn: IsoDate.nullable().optional(),
  position: z.number().int().min(0).max(10_000).optional(),
  status: z.enum(['planned', 'active']).optional().describe('reached follows from the tasks and issues'),
  amountCents: Cents.nullable().optional(),
  forgeMilestoneIds: z.array(Id).max(10).optional(),
};

export const listMilestones = defineAction({
  name: 'project.milestone.list',
  method: 'get',
  path: '/project/milestones',
  permission: P.keys.read,
  input: ListQuery.extend({
    projectId: Id.optional().describe('Milestones of this project and its subprojects'),
    status: CsvEnum(MILESTONE_STATUSES).optional(),
    late: QueryBool.optional(),
    unaccepted: QueryBool.optional().describe('Reached or with an amount, and not accepted yet'),
  }),
  output: Paginated(Milestone),
  summary: 'List milestones, by due date (CO-0433)',
});

export const getMilestone = defineAction({
  name: 'project.milestone.get',
  method: 'get',
  path: '/project/milestones/{id}',
  permission: P.keys.read,
  input: z.object({ id: Id }),
  output: Milestone,
  etag: true,
});

export const createMilestone = defineAction({
  name: 'project.milestone.create',
  method: 'post',
  path: '/project/milestones',
  permission: P.keys.create,
  input: z.object({ projectId: Id, ...milestoneFields }).strict(),
  output: Milestone,
  summary: 'Add a milestone to a project: a date with a result, and for a fixed price its amount (CO-0433)',
});

export const updateMilestone = defineAction({
  name: 'project.milestone.update',
  method: 'patch',
  path: '/project/milestones/{id}',
  permission: P.keys.update,
  input: z
    .object({
      id: Id,
      ...updateFields(milestoneFields),
      expectedVersion: ExpectedVersion,
    })
    .strict(),
  output: Milestone,
  etag: true,
});

export const deleteMilestone = defineAction({
  name: 'project.milestone.delete',
  method: 'delete',
  path: '/project/milestones/{id}',
  permission: P.keys.delete,
  input: z.object({ id: Id, expectedVersion: QueryVersion }),
  output: z.object({ ok: z.literal(true) }),
  etag: true,
  summary: 'Delete a milestone without tasks that is not accepted',
});

export const acceptMilestone = defineAction({
  name: 'project.milestone.accept',
  method: 'post',
  path: '/project/milestones/{id}/accept',
  permission: P.keys.approve,
  input: z
    .object({
      id: Id,
      acceptedByName: z.string().trim().min(1).max(200).describe('Who accepted it at the client'),
      acceptedOn: IsoDate.optional().describe('Default today'),
      note: z.string().max(5000).optional(),
    })
    .strict(),
  output: Milestone,
  successStatus: 200,
  summary: 'Record that the client accepted a milestone; a fixed amount becomes an invoice term (CO-0433, CO-0342)',
  description: 'Only a person records this (projects:approve); an agent never does.',
});

export const milestoneChanged = defineEvent({
  type: 'project.milestone.changed',
  version: 1,
  payload: z.object({ milestoneId: Id, projectId: Id, change: z.enum(['created', 'updated', 'deleted', 'reached', 'reopened']) }),
});

export const milestoneAccepted = defineEvent({
  type: 'project.milestone.accepted',
  version: 1,
  payload: z.object({
    milestoneId: Id,
    projectId: Id,
    engagementId: Id.nullable(),
    organisationId: Id.nullable(),
    title: z.string(),
    amountCents: z.number().int().nullable(),
    currency: z.string(),
    acceptedOn: IsoDate,
  }),
});

export const milestoneActions = [listMilestones, getMilestone, createMilestone, updateMilestone, deleteMilestone, acceptMilestone];
export const milestoneEvents = [milestoneChanged, milestoneAccepted];

// ---------- change requests (U5, CO-1338) ----------

export const CHANGE_REQUEST_STATUSES = ['draft', 'sent', 'accepted', 'rejected'] as const;
export const ChangeRequestStatus = z.enum(CHANGE_REQUEST_STATUSES);

export const ChangeRequest = z
  .object({
    id: Id,
    number: z.number().int(),
    key: z.string().describe('CR-7'),
    projectId: Id.nullable(),
    projectName: z.string().nullable(),
    engagementId: Id.nullable(),
    engagementName: z.string().nullable(),
    organisationId: Id.nullable(),
    title: z.string(),
    description: z.string().describe('Markdown'),
    impactMinutes: z.number().int().nullable(),
    impactCents: z.number().int().nullable(),
    currency: z.string(),
    status: ChangeRequestStatus,
    sentAt: IsoDateTime.nullable(),
    decidedAt: IsoDateTime.nullable(),
    decidedByName: z.string().nullable().describe('Who decided at the client'),
    decisionNote: z.string(),
    issueIds: z.array(Id),
    milestoneId: Id.nullable().describe('The milestone it became, when accepted as one'),
    createdAt: IsoDateTime,
    version: z.number().int(),
    url: z.string(),
  })
  .openapi('ChangeRequest');

const changeRequestFields = {
  title: z.string().trim().min(1).max(300),
  description: z.string().max(50_000).optional(),
  impactMinutes: Minutes.nullable().optional().describe('Extra hours, in minutes'),
  impactCents: Cents.nullable().optional().describe('Extra money, in cents'),
  issueIds: z.array(Id).max(50).optional().describe('Issues of the mirror it is about (dev.issue.list)'),
};

export const listChangeRequests = defineAction({
  name: 'project.changeRequest.list',
  method: 'get',
  path: '/project/change-requests',
  permission: P.keys.read,
  input: ListQuery.extend({
    projectId: Id.optional(),
    engagementId: Id.optional(),
    status: CsvEnum(CHANGE_REQUEST_STATUSES).optional(),
  }),
  output: Paginated(ChangeRequest),
  summary: 'List change requests, newest first (CO-1338)',
});

export const getChangeRequest = defineAction({
  name: 'project.changeRequest.get',
  method: 'get',
  path: '/project/change-requests/{id}',
  permission: P.keys.read,
  input: z.object({ id: Id }),
  output: ChangeRequest,
  etag: true,
});

export const createChangeRequest = defineAction({
  name: 'project.changeRequest.create',
  method: 'post',
  path: '/project/change-requests',
  permission: P.keys.create,
  input: z
    .object({
      projectId: Id.nullable().optional().describe('The project; its engagement comes along'),
      engagementId: Id.nullable().optional().describe('Without a project: the engagement'),
      ...changeRequestFields,
    })
    .strict(),
  output: ChangeRequest,
  summary: 'Add a change request: extra work or a change with its impact in hours and money (CO-1338)',
});

export const updateChangeRequest = defineAction({
  name: 'project.changeRequest.update',
  method: 'patch',
  path: '/project/change-requests/{id}',
  permission: P.keys.update,
  input: z
    .object({
      id: Id,
      ...updateFields(changeRequestFields),
      expectedVersion: ExpectedVersion,
    })
    .strict(),
  output: ChangeRequest,
  etag: true,
  summary: 'Change a change request that is not decided; a sent one goes back to draft',
});

export const sendChangeRequest = defineAction({
  name: 'project.changeRequest.send',
  method: 'post',
  path: '/project/change-requests/{id}/send',
  permission: P.keys.update,
  input: z
    .object({
      id: Id,
      to: z.array(z.email().max(320)).max(20).optional().describe('Default: the contacts of the client with an e-mail address'),
      message: z.string().max(5000).optional().describe('A line of your own above the change request'),
    })
    .strict(),
  output: z.object({ changeRequest: ChangeRequest, draftId: z.string(), webLink: z.string().nullable(), to: z.array(z.string()) }),
  successStatus: 200,
  summary: 'Put the change request as a draft mail to the client in your mailbox; it is never sent by the app (CO-1338)',
});

export const decideChangeRequest = defineAction({
  name: 'project.changeRequest.decide',
  method: 'post',
  path: '/project/change-requests/{id}/decide',
  permission: P.keys.approve,
  input: z
    .object({
      id: Id,
      decision: z.enum(['accepted', 'rejected']),
      decidedByName: z.string().trim().min(1).max(200).describe('Who decided at the client'),
      decidedOn: IsoDate.optional().describe('Default today'),
      note: z.string().max(5000).optional(),
      asMilestone: z.boolean().default(false).describe('Accepted: make a milestone with the amount instead of raising the budget; needs a project'),
      milestoneDueOn: IsoDate.optional(),
    })
    .strict(),
  output: ChangeRequest,
  successStatus: 200,
  summary: "Record the client's decision on a change request; accepted raises the budget or becomes a milestone (CO-1338)",
  description: 'Only a person records this (projects:approve); an agent never does.',
});

export const changeRequestChanged = defineEvent({
  type: 'project.changeRequest.changed',
  version: 1,
  payload: z.object({ changeRequestId: Id, projectId: Id.nullable(), engagementId: Id.nullable(), change: z.enum(['created', 'updated', 'sent']) }),
});

export const changeRequestDecided = defineEvent({
  type: 'project.changeRequest.decided',
  version: 1,
  payload: z.object({
    changeRequestId: Id,
    projectId: Id.nullable(),
    engagementId: Id.nullable(),
    decision: z.enum(['accepted', 'rejected']),
    impactMinutes: z.number().int().nullable(),
    impactCents: z.number().int().nullable(),
    milestoneId: Id.nullable(),
  }),
});

export const changeRequestActions = [listChangeRequests, getChangeRequest, createChangeRequest, updateChangeRequest, sendChangeRequest, decideChangeRequest];
export const changeRequestEvents = [changeRequestChanged, changeRequestDecided];

// ---------- weekly status (U5, CO-0436, CO-0197) ----------

export const STATUS_UPDATE_SOURCES = ['person', 'agent'] as const;

export const StatusUpdate = z
  .object({
    id: Id,
    projectId: Id,
    projectName: z.string().nullable(),
    week: IsoDate.describe('The Monday of the week'),
    health: ProjectHealth,
    summary: z.string().describe('Markdown'),
    source: z.enum(STATUS_UPDATE_SOURCES).describe('Written by a person, or drafted by an agent (the weekly routine)'),
    clientVisible: z.boolean().describe('Shared with the client in the portal (CO-0665)'),
    createdById: Id,
    createdByName: z.string().nullable(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
    version: z.number().int(),
  })
  .openapi('ProjectStatusUpdate');

export const listStatusUpdates = defineAction({
  name: 'project.statusUpdate.list',
  method: 'get',
  path: '/project/status-updates',
  permission: P.keys.read,
  input: ListQuery.extend({
    projectId: Id.optional(),
    from: IsoDate.optional().describe('From the week of this date'),
    to: IsoDate.optional().describe('Weeks starting on or before this date'),
    health: CsvEnum(PROJECT_HEALTHS).optional(),
  }),
  output: Paginated(StatusUpdate),
  summary: 'The weekly statuses of projects, newest week first (CO-0436)',
});

export const createStatusUpdate = defineAction({
  name: 'project.statusUpdate.create',
  method: 'post',
  path: '/project/status-updates',
  permission: P.keys.update,
  input: z
    .object({
      projectId: Id,
      week: IsoDate.optional().describe('Any day of the week; default this week. Stored as its Monday'),
      health: ProjectHealth,
      summary: z.string().trim().min(1).max(20_000).describe('Markdown: progress, hours against budget, issues, risks'),
      healthNote: z.string().trim().max(500).optional().describe('One line for the overview; default the first line of the summary'),
      clientVisible: z.boolean().default(false).describe('Share it with the client in the portal (CO-0665)'),
    })
    .strict(),
  output: StatusUpdate,
  summary: 'Write the weekly status of a project; a second one in the same week replaces the first (CO-0436)',
  description: 'The latest week sets the health of the project (CO-0197). An agent writes it as the weekly routine; its source is agent.',
});

export const statusUpdateCreated = defineEvent({
  type: 'project.statusUpdate.created',
  version: 1,
  payload: z.object({ statusUpdateId: Id, projectId: Id, week: IsoDate, health: ProjectHealth, replaced: z.boolean() }),
});

export const statusUpdateActions = [listStatusUpdates, createStatusUpdate];

// ---------- my work (U5, CO-0441) ----------

export const MyWorkItem = z
  .object({
    type: z.enum(['task', 'issue']).describe('A follow-up on a lead, organisation or engagement is a task (CO-1390)'),
    id: Id,
    key: z.string().describe('T-12 for a task, owner/repo#12 for an issue'),
    title: z.string(),
    projectId: Id.nullable(),
    projectName: z.string().nullable(),
    on: z
      .object({ type: z.enum(TASK_PARENTS), id: Id, label: z.string().nullable() })
      .nullable()
      .describe('What a task without project is on: its engagement, organisation or lead'),
    dueAt: IsoDateTime.nullable().describe('Always null; kept for compatibility (CO-1390)'),
    dueOn: IsoDate.nullable().describe('A task: its due date. An issue: the due date of its milestone'),
    overdue: z.boolean(),
    priority: z.enum(TASK_PRIORITIES).nullable().describe('Tasks only'),
    milestoneTitle: z.string().nullable().describe('Issues only: its milestone'),
  })
  .openapi('MyWorkItem');

export const MyWork = z
  .object({
    today: z.array(MyWorkItem).describe('Due today, or late'),
    week: z.array(MyWorkItem).describe('Due later this week (up to Sunday)'),
    later: z.array(MyWorkItem).describe('Due after this week, or without a date'),
  })
  .openapi('MyWork');

export const getMyWork = defineAction({
  name: 'project.work.mine',
  method: 'get',
  path: '/project/my-work',
  permission: AUTHENTICATED,
  input: z.object({}),
  output: MyWork,
  summary: 'My open tasks (follow-ups on leads, organisations and engagements included) and the Forgejo issues assigned to me, by when they are due (CO-0441, CO-0435)',
  description:
    'Tasks need projects:read, issues dev:read and a linked Forgejo account (Settings, Connections); without them that part is empty. An agent that runs for a person gets the work of that person.',
});

export const projectEvents = [projectChanged, projectMoved, workTypeChanged, ...taskEvents, ...milestoneEvents, ...changeRequestEvents, statusUpdateCreated];

// ---------------------------------------------------------------- overview (U9, CO-1324)

export const projectOverview = defineAction({
  name: 'project.overview.get',
  method: 'get',
  path: '/project/overview',
  permission: P.keys.read,
  input: z.object({}),
  output: z.object({
    statuses: z.array(z.object({ status: z.enum(PROJECT_STATUSES), count: z.number().int() })),
    health: z.array(z.object({ health: z.enum([...PROJECT_HEALTHS, 'none']), count: z.number().int() })).describe('Of the active projects'),
    overdueTasks: z.number().int().describe('Open tasks past their due date'),
    milestonesThisMonth: z.number().int().describe('Milestones due this month and not reached'),
    lateMilestones: z.number().int().describe('Milestones past their due date and not reached'),
  }),
  summary: 'Projects at a glance: per status and health, overdue tasks and the milestones of this month (CO-1324); budgets above 90% are in report.budget.status',
});
